import { ChevronDown, ChevronUp } from "lucide-react";
import React from "react";
import { useDropdownPosition, useRenderDropdownInPortal, TDropdownPlacement, useIsMounted } from "../../../hooks";
import { TInputLikeProps } from "../InputLike";
import * as S from "./styled";

export interface IBaseDropdownProps extends Partial<TInputLikeProps> {
    disabled?: boolean;
    withoutArrow?: boolean;
    withClearButton?: boolean;
    children: React.ReactNode;
    dropdownContent: React.ReactNode;
    inPortal?: boolean;
    portalPlacement?: TDropdownPlacement;
    onDropdownOpen?: () => void;
    onDropdownClose?: () => void;
}

export type BaseDropdownRef = {
    open: () => void;
    close: () => void;
};

const BaseDropdown = React.forwardRef<BaseDropdownRef, IBaseDropdownProps>(
    (
        {
            icon,
            disabled = false,
            children,
            dropdownContent,
            inPortal = false,
            withoutArrow = false,
            portalPlacement = "auto",
            onDropdownOpen,
            onDropdownClose,
            trail,
            clear,
            withClearButton = false,
            ...props
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = React.useState<boolean>(false);
        const isMounted = useIsMounted();
        const selectRef = React.useRef<HTMLDivElement>(null);
        const dropdownRef = React.useRef<HTMLDivElement>(null);
        const onDropdownCloseRef = React.useRef<() => void>();
        const dropdownPosition = useDropdownPosition({
            anchorRef: selectRef,
            isOpen,
            dropdownRef,
            placement: portalPlacement
        });
        const render = useRenderDropdownInPortal(dropdownPosition, inPortal);

        const handleDropdownOpen = React.useCallback(() => {
            setIsOpen(true);
            onDropdownOpen?.();
        }, [onDropdownOpen]);

        const handleDropdownClose = React.useCallback(() => {
            setIsOpen(false);
            onDropdownClose?.();
        }, [onDropdownClose]);

        React.useEffect(() => {
            onDropdownCloseRef.current = handleDropdownClose;
        }, [handleDropdownClose]);

        React.useImperativeHandle(
            ref,
            () => ({
                open: () => {
                    if (disabled) return;

                    handleDropdownOpen();
                },
                close: () => {
                    if (disabled) return;

                    handleDropdownClose();
                }
            }),
            [disabled, handleDropdownOpen, handleDropdownClose]
        );

        React.useEffect(() => {
            const checkEventOutside = (e: MouseEvent) => {
                const insideSelect = !!selectRef.current?.contains(e.target as Node);
                if (!inPortal) {
                    return !insideSelect;
                }
                const insideDD = !!dropdownRef.current?.contains(e.target as Node);

                return !insideSelect && !insideDD;
            };
            const handleClickOutside = (e: MouseEvent) => {
                if (isOpen && checkEventOutside(e) && isMounted()) {
                    onDropdownCloseRef.current?.();
                }
            };

            document.addEventListener("mousedown", handleClickOutside, false);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, [isOpen, selectRef, ref]);

        return (
            <S.SelectWrapper ref={selectRef}>
                <S.InputLike
                    {...props}
                    icon={icon}
                    disabled={disabled}
                    focused={isOpen}
                    onClick={e => {
                        if (disabled) return;

                        e.preventDefault();
                        isOpen ? handleDropdownClose() : handleDropdownOpen();
                    }}
                    trail={
                        trail ??
                        (!withoutArrow && (
                            <S.ArrowWrapper withClearButton={withClearButton}>
                                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </S.ArrowWrapper>
                        ))
                    }
                    clear={clear}
                >
                    <S.DropdownContent>{children}</S.DropdownContent>
                </S.InputLike>
                {isOpen && render(<S.DropdownWrapper ref={dropdownRef}>{dropdownContent}</S.DropdownWrapper>)}
            </S.SelectWrapper>
        );
    }
);

export default BaseDropdown;
